import React, { useEffect, useMemo, useRef, useState } from "react";
import Excel from "../Image/excel.png";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useDispatch, useSelector } from "react-redux";
import Messagedialogbox, { Show_Message } from "../General/Messagedialogbox";
import ReportSetting from "./ReportSetting";
import ReportFilter from "./ReportFilter";
import { API_FETCH, API_POST } from "../General/Utility";
import CustomizedSelectoption from "../General/CustomizedSelectoption";
import { hide_Options } from "../features/Customeselect";

//#region Shared helpers (used by GeneralReports / AccountsReport)
const Zero_Pad = (strValue, iLen) => {
  let str = String(strValue);
  while (str.length < iLen) str = "0" + str;
  return str;
};
const Pad2 = (n) => Zero_Pad(n, 2);
// Local calendar date as yyyy-MM-dd (toISOString() gives the UTC date: wrong before 05:30 IST)
export const Today_String = () => {
  const d = new Date();
  return `${d.getFullYear()}-${Pad2(d.getMonth() + 1)}-${Pad2(d.getDate())}`;
};
// Any server date string -> yyyy-MM-dd for <input type="date">; "" if it cannot be read
export const To_DateInput = (strValue) => {
  const str = String(strValue || "").trim();
  if (!str) return "";
  let m = str.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (m) return `${m[1]}-${Pad2(m[2])}-${Pad2(m[3])}`;
  m = str.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})/); // dd-MM-yyyy (en-IN)
  if (m) return `${m[3]}-${Pad2(m[2])}-${Pad2(m[1])}`;
  return "";
};
// Strict date for the API: a real calendar date, year 1900-2999, as "yyyy-MM-dd"; "" otherwise.
// (While a year is being typed the browser reports 0002-04-01, 0020-04-01 ... - those are rejected.)
export const Norm_Date = (Value) => {
  const str = To_DateInput(Value);
  if (!str) return "";
  const iY = Number(str.slice(0, 4));
  const iM = Number(str.slice(5, 7));
  const iD = Number(str.slice(8, 10));
  if (iY < 1900 || iY > 2999 || iM < 1 || iM > 12 || iD < 1) return "";
  const dt = new Date(iY, iM - 1, iD);
  return dt.getFullYear() === iY && dt.getMonth() === iM - 1 && dt.getDate() === iD ? str : ""; // 31-02 -> ""
};
// One rule for EVERY report request (General + Accounts screens, view, page, sort and download):
//   ids (report / party / branch / warehouse) -> whole number >= 0  (0 = not selected / all)
//   page -> whole number >= 1
//   text inputs -> string, never null / undefined
//   dates -> "yyyy-MM-dd" (Norm_Date), and never half a range: both dates or none
// Sort column / order travel in the query string, so they are removed from the body.
const Norm_Int = (Value, iMin) => {
  const n = Math.floor(Number(Value));
  return isFinite(n) && n >= iMin ? n : iMin;
};
const Norm_Text = (Value) => (Value === null || Value === undefined ? "" : String(Value));
export const Normalize_ReportQuery = (query) => {
  const objBody = { ...(query || {}) };
  delete objBody.strSortCol;
  delete objBody.strSortOrder;
  ["iRptId", "iPartyId", "iBranchId", "iWHId"].forEach((k) => {
    objBody[k] = Norm_Int(objBody[k], 0);
  });
  objBody.CurrtPage = Norm_Int(objBody.CurrtPage, 1);
  ["strSearch", "strColName", "strPartyName", "strLedger"].forEach((k) => {
    objBody[k] = Norm_Text(objBody[k]);
  });
  objBody.dpFrom = Norm_Date(objBody.dpFrom);
  objBody.dpTo = Norm_Date(objBody.dpTo);
  if (!objBody.dpFrom || !objBody.dpTo) {
    objBody.dpFrom = "";
    objBody.dpTo = "";
  }
  return objBody;
};
// Financial-year start from General Settings; falls back to 1-April of the current FY
export const FinYear_String = (strServerDate) => {
  const strDate = To_DateInput(strServerDate);
  if (strDate) return strDate;
  const d = new Date();
  const iYear = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${iYear}-04-01`;
};
// true when the API answered with data; shows the server message on strStatus "error"
export const Is_ValidResponse = (res, dispatch) => {
  if (!res) return false;
  if (typeof res.strStatus === "string" && res.strStatus.toLowerCase() === "error") {
    Show_Message(dispatch, res.strMessage || "Something went wrong", "error");
    return false;
  }
  return true;
};
// Server sends rows as a JSON string, or null when there are no rows
export const Parse_Json = (DataArray) => {
  if (DataArray === null || DataArray === undefined || DataArray === "") return [];
  if (Array.isArray(DataArray)) return DataArray;
  try {
    const arr = JSON.parse(DataArray);
    return Array.isArray(arr) ? arr : [];
  } catch (e) {
    return [];
  }
};
export const Sort_Design = (EntArr) => {
  if (!Array.isArray(EntArr)) return [];
  return EntArr.slice()
    .sort((a, b) => Number(a.inOrderTo) - Number(b.inOrderTo))
    .filter((rdt) => rdt.isShowInReport);
};
const Type_Of = (strType) => {
  const t = String(strType || "").replace(/\t/g, "").trim().toLowerCase();
  if (t === "datetime" || t === "date") return "DateTime";
  if (t === "decimal") return "decimal";
  return "String";
};
const Date_Value = (Value) => {
  const str = String(Value || "").trim();
  let m = str.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (m) return new Date(m[1], m[2] - 1, m[3], m[4] || 0, m[5] || 0, m[6] || 0).getTime();
  m = str.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (m) return new Date(m[3], m[2] - 1, m[1], m[4] || 0, m[5] || 0, m[6] || 0).getTime();
  return Number.NEGATIVE_INFINITY;
};
// Exact money arithmetic. Values arrive as decimal strings (e.g. "1.00500"); Number().toFixed(2) is binary
// floating point and shows 1.005 as 1.00 and 2.675 as 2.67. These helpers keep every value as an exact
// decimal: { bNeg, strMag } where strMag is the absolute value x 10^DEC_SCALE as a digit string with no
// leading zeros ("0" for zero). Plain ES2015 string arithmetic only - no BigInt, no padStart, no polyfills.
const DEC_SCALE = 6; // MySQL report columns use at most 5 decimals
const DEC_ZERO = { bNeg: false, strMag: "0" };
// drop leading zeros; empty -> "0"
const Mag_Trim = (strMag) => {
  let i = 0;
  while (i < strMag.length - 1 && strMag.charAt(i) === "0") i++;
  return strMag.slice(i) || "0";
};
// compare two magnitudes (no leading zeros): -1 / 0 / 1
const Mag_Cmp = (strA, strB) => {
  if (strA.length !== strB.length) return strA.length < strB.length ? -1 : 1;
  return strA < strB ? -1 : strA > strB ? 1 : 0; // same length digit strings compare lexically
};
// strA + strB (school addition, right to left)
const Mag_Add = (strA, strB) => {
  let i = strA.length - 1;
  let j = strB.length - 1;
  let iCarry = 0;
  const arrOut = [];
  while (i >= 0 || j >= 0 || iCarry > 0) {
    const iSum = (i >= 0 ? strA.charCodeAt(i) - 48 : 0) + (j >= 0 ? strB.charCodeAt(j) - 48 : 0) + iCarry;
    arrOut.push(iSum % 10);
    iCarry = iSum >= 10 ? 1 : 0;
    i--;
    j--;
  }
  return Mag_Trim(arrOut.reverse().join(""));
};
// strA - strB, caller guarantees strA >= strB
const Mag_Sub = (strA, strB) => {
  let i = strA.length - 1;
  let j = strB.length - 1;
  let iBorrow = 0;
  const arrOut = [];
  while (i >= 0) {
    let iDiff = strA.charCodeAt(i) - 48 - (j >= 0 ? strB.charCodeAt(j) - 48 : 0) - iBorrow;
    if (iDiff < 0) {
      iDiff += 10;
      iBorrow = 1;
    } else iBorrow = 0;
    arrOut.push(iDiff);
    i--;
    j--;
  }
  return Mag_Trim(arrOut.reverse().join(""));
};
// "1,23,456.785" / "-.5" / "+12" / 12.5 -> exact decimal; null when not a number
const Dec_Parse = (Value) => {
  if (Value === null || Value === undefined) return null;
  const m = String(Value).trim().replace(/,/g, "").match(/^([+-])?(\d*)(?:\.(\d*))?$/);
  if (!m || (m[2] === "" && (m[3] === undefined || m[3] === ""))) return null;
  let strFrac = (m[3] || "").slice(0, DEC_SCALE);
  while (strFrac.length < DEC_SCALE) strFrac += "0";
  const strMag = Mag_Trim((m[2] || "0") + strFrac);
  return { bNeg: m[1] === "-" && strMag !== "0", strMag: strMag };
};
const Dec_Add = (a, b) => {
  if (a.bNeg === b.bNeg) return { bNeg: a.bNeg, strMag: Mag_Add(a.strMag, b.strMag) };
  const iCmp = Mag_Cmp(a.strMag, b.strMag);
  if (iCmp === 0) return DEC_ZERO;
  return iCmp > 0
    ? { bNeg: a.bNeg, strMag: Mag_Sub(a.strMag, b.strMag) }
    : { bNeg: b.bNeg, strMag: Mag_Sub(b.strMag, a.strMag) };
};
const Dec_Cmp = (a, b) => {
  if (a.bNeg !== b.bNeg) return a.bNeg ? -1 : 1;
  const iCmp = Mag_Cmp(a.strMag, b.strMag);
  return a.bNeg ? -iCmp : iCmp;
};
const Dec_IsNeg = (dec) => dec !== null && dec.bNeg && dec.strMag !== "0";
// round half away from zero to 2 places (the commercial rule, = .NET MidpointRounding.AwayFromZero)
const Dec_Format2 = (dec) => {
  if (dec === null) return "";
  const strFull = Zero_Pad(dec.strMag, DEC_SCALE + 1); // at least one integer digit
  const iCut = strFull.length - (DEC_SCALE - 2); // keep integer part + 2 decimals
  let strPaise = Mag_Trim(strFull.slice(0, iCut)); // magnitude in paise, truncated
  if (strFull.charCodeAt(iCut) - 48 >= 5) strPaise = Mag_Add(strPaise, "1"); // first dropped digit >= 5 -> up
  const strAbs = Zero_Pad(strPaise, 3);
  const strOut = strAbs.slice(0, -2) + "." + strAbs.slice(-2);
  return (dec.bNeg && strPaise !== "0" ? "-" : "") + strOut; // -0.004 -> "0.00", never "-0.00"
};
export const Round2 = (Value) => {
  const dec = Dec_Parse(Value);
  return dec === null ? null : Dec_Format2(dec);
};
const Escape_Html = (Value) =>
  String(Value === null || Value === undefined ? "" : Value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
//#endregion

// Ctrl+P / browser print: only the report grid, header repeated on every sheet, no split rows, full borders.
const Print_Css = `
.rpt-print-only { display: none; }
@media print {
  @page { size: A4 landscape; margin: 10mm; }
  body * { visibility: hidden !important; }
  .rpt-print-area, .rpt-print-area * { visibility: visible !important; }
  .rpt-print-area { position: absolute; left: 0; top: 0; width: 100%; }
  .rpt-print-area .report-header, .rpt-print-area .rpt-section, .rpt-print-area .icon-btn-grp,
  .rpt-print-area .popup-container { display: none !important; }
  .rpt-print-only { display: block !important; text-align: center; margin-bottom: 6px; }
  .rpt-print-only h3 { margin: 0; font-size: 14pt; }
  .rpt-print-only p { margin: 2px 0 0; font-size: 9pt; }
  .rpt-print-area .table-card { overflow: visible !important; height: auto !important; max-height: none !important; }
  #Reporttable { width: 100% !important; border-collapse: collapse !important; font-size: 8pt; }
  #Reporttable th, #Reporttable td { border: 1px solid #000 !important; padding: 2px 4px !important;
    color: #000 !important; background: #fff !important; white-space: normal !important;
    width: auto !important; min-width: 0 !important; max-width: none !important; }
  #Reporttable thead { display: table-header-group; }
  #Reporttable tfoot { display: table-row-group; font-weight: bold; }
  #Reporttable tr { page-break-inside: avoid; break-inside: avoid; }
}`;

function ReportTable(props) {
  const dispatch = useDispatch();
  const _AuthParem = useSelector((state) => state.Common.Admin);
  const _select = useSelector((state) => state.SelectOption.parems);

  const [strRptSearch, setstrRptSearch] = useState("");
  const [strColSearch, setstrColSearch] = useState("");
  const [strCustSearch, setstrCustSearch] = useState("");
  const [strtableSearch, setstrtableSearch] = useState("");
  const [IsSetting, setIsSetting] = useState(false);
  const [IsFilter, setIsFilter] = useState(false);
  const [IsColOption, setIsColOption] = useState(false);
  const [IsExporting, setIsExporting] = useState(false);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [RptColumn, setRptColumn] = useState([]);
  const [strLedSearch, setstrLedSearch] = useState("");
  const [strPageInput, setstrPageInput] = useState("1");
  const [IsClearingFilters, setIsClearingFilters] = useState(false);
  const [strRemovingCol, setstrRemovingCol] = useState(""); // filter chip being removed
  const FilterBtnRef = useRef(null);
  const FilterBusy = useRef(false); // one filter change (chip x / clear all) at a time - no double requests

  const RptSearchTimer = useRef(null);
  const TableSearchTimer = useRef(null);
  const PartySearchTimer = useRef(null);

  // clear pending timers when the screen closes (no setState after unmount)
  useEffect(() => {
    return () => {
      clearTimeout(RptSearchTimer.current);
      clearTimeout(TableSearchTimer.current);
      clearTimeout(PartySearchTimer.current);
    };
  }, []);
  useEffect(() => {
    setstrPageInput(String(props.CurrPage || 1));
  }, [props.CurrPage]);
  // a new report / new data set: drop the client-side sort of the previous one,
  // and close the filter panel (it belongs to the previous report)
  useEffect(() => {
    setSortBy(null);
    setSortOrder("asc");
    setIsFilter(false);
  }, [props.iRptId]);
  useEffect(() => {
    if (props.IsFilterHidden) setIsFilter(false);
  }, [props.IsFilterHidden]);

  const AlertMessage = (Msg, type) => {
    Show_Message(dispatch, Msg, type);
  };
  const ReportData = Array.isArray(props.ReportData) ? props.ReportData : [];
  const ReportDesing = Array.isArray(props.ReportDesing) ? props.ReportDesing : [];
  const EntSum = Array.isArray(props.EntSum) ? props.EntSum : [];
  const TotalRows = Number(props.TotalRows) || 0;
  const RowPerPage = Number(props.RowPerPage) || 0;
  const CurrPage = Number(props.CurrPage) || 1;
  const TotalPages = props.IsPaged && RowPerPage > 0 ? Math.max(1, Math.ceil(TotalRows / RowPerPage)) : 1;
  const IsPartyPending = !props.IsAccounts && props.IsParty && !props.PartyName;
  const UserQs = `?iUserId=${Number(_AuthParem.User) || 0}`; // filters / search column are checked per user
  // General Ledger (1015) shows one ledger at a time: nothing is fetched until a ledger is chosen
  const IsLedgerPending =
    Boolean(props.IsAccounts) && Number(props.iRptId) === 1015 && !String(props.strLedger || "").trim();

  //#region Search
  const Search_ReportList = (val) => {
    setstrRptSearch(val);
    clearTimeout(RptSearchTimer.current);
    RptSearchTimer.current = setTimeout(() => props.Load_Reports(val), 300);
  };
  const Run_TableSearch = (val) => {
    clearTimeout(TableSearchTimer.current);
    if (String(val).trim().length > 0 && !props.FilterColName) {
      AlertMessage("Please select a search column", "info");
      return;
    }
    props.Search_Report(val, props.FilterColName);
  };
  const Search_Table = (val) => {
    setstrtableSearch(val);
    clearTimeout(TableSearchTimer.current);
    TableSearchTimer.current = setTimeout(() => Run_TableSearch(val), 300);
  };
  const Save_SearchColumn = async (col) => {
    try {
      setIsColOption(false);
      setstrColSearch("");
      props.setstrFilterColName(col.strRptColName);
      const res = await API_POST(
        `Reports/Insert_SearchCol/${Number(props.iRptId)}/${encodeURIComponent(
          col.strRptColName
        )}/${_AuthParem.DB}${UserQs}`,
        {},
        dispatch
      );
      Is_ValidResponse(res, dispatch);
      if (strtableSearch.trim().length > 0)
        props.Search_Report(strtableSearch, col.strRptColName);
    } catch (err) {
      AlertMessage(err.message, "error");
    }
  };
  const Search_FilterCol = (val) => {
    setstrColSearch(val);
    const strVal = String(val || "").toLowerCase();
    setRptColumn(
      ReportDesing.filter(
        (dt) =>
          String(dt.strRptColName || "").toLowerCase().includes(strVal) ||
          String(dt.strAliasName || "").toLowerCase().includes(strVal)
      )
    );
  };
  // Ledger picker: matches ledger name, ledger code and alias (e.g. "hdfc" finds "Bank - HDFC A/c" by its alias).
  // Order: name starts with the text, name contains it, then code / alias matches; list order kept inside each.
  const Search_Ledger = (val) => {
    const EntLedger = Array.isArray(props.EntLedger) ? props.EntLedger : [];
    const strFind = String(val || "").trim().toLowerCase();
    const Low = (v) => String(v === null || v === undefined ? "" : v).toLowerCase();
    const arrRanked = [];
    EntLedger.forEach((dt, iIndex) => {
      if (!strFind) {
        arrRanked.push({ dt: dt, iRank: 0, iIndex: iIndex });
        return;
      }
      const strName = Low(dt.strName);
      let iRank = -1;
      if (strName.indexOf(strFind) === 0) iRank = 0;
      else if (strName.indexOf(strFind) > 0) iRank = 1;
      else if (Low(dt.strCode).indexOf(strFind) >= 0 || Low(dt.strAlias).indexOf(strFind) >= 0) iRank = 2;
      if (iRank >= 0) arrRanked.push({ dt: dt, iRank: iRank, iIndex: iIndex });
    });
    arrRanked.sort((a, b) => a.iRank - b.iRank || a.iIndex - b.iIndex);
    props.setarrLedgerOption(arrRanked.map((x) => x.dt));
    setstrLedSearch(val);
  };
  const Search_PartyText = (val) => {
    setstrCustSearch(val);
    clearTimeout(PartySearchTimer.current);
    PartySearchTimer.current = setTimeout(() => props.Search_Party(val), 300);
  };
  const Select_Party = (Party) => {
    if (!Party) return;
    setstrCustSearch("");
    props.View_VendoreOutstanding(Party);
  };
  const View_Ledger = (strLedName) => {
    setstrLedSearch("");
    props.setIsLedOption(false);
    props.Ledger_Change(strLedName);
  };
  const FilterColAlias = () => {
    const objCol = ReportDesing.find((dt) => dt.strRptColName === props.FilterColName);
    return objCol ? objCol.strAliasName : props.FilterColName || "";
  };
  //#endregion

  //#region Report list
  const Open_Report = (report) => {
    clearTimeout(TableSearchTimer.current);
    setstrColSearch("");
    setstrCustSearch("");
    setstrtableSearch("");
    setIsColOption(false);
    props.Party_OptionShow(false);
    if (props.IsAccounts) props.Clear_Report();
    props.View_Report(
      report.iId,
      report.strName,
      props.iBranchId,
      props.iWHId,
      props.IsAccounts ? "" : props.strLedger,
      props.strFromDate,
      props.strToDate
    );
    if (Number(report.iId) === 1015 && props.IsAccounts) props.Load_Ledger();
  };
  const Focus_Sibling = (e, iIndex) => {
    const objParent = e.currentTarget.parentElement;
    const objNext = objParent && objParent.querySelector(`[tabindex="${iIndex}"]`);
    if (objNext) objNext.focus();
  };
  const List_KeyDown = (e, Index, arrLength, onEnter) => {
    const iLast = arrLength - 1;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      Focus_Sibling(e, Index >= iLast ? 0 : Index + 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      Focus_Sibling(e, Index <= 0 ? iLast : Index - 1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      onEnter();
    }
  };
  //#endregion

  //#region Dates / paging / sorting
  const Date_Onchange = (Data, DateFor) => {
    if (!Data) return;
    if (DateFor === "FromDate") props.Date_Change(Data, props.strToDate);
    else props.Date_Change(props.strFromDate, Data);
  };
  const Go_Page = (iPage) => {
    const iTarget = Math.min(Math.max(1, Number(iPage) || 1), TotalPages);
    setstrPageInput(String(iTarget));
    if (iTarget !== CurrPage) props.View_PageReport(iTarget);
  };
  const Sort_Report = (design) => {
    // accounting statements (running balance / Dr | Cr layout) keep their own row order
    if (props.IsSortLocked) return;
    const key = design.strRptColName;
    if (props.IsSortable && props.IsPaged && TotalPages > 1) {
      // all rows are not on screen: let the server sort
      props.Sort_Report(key);
      return;
    }
    if (sortBy === key) setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    else {
      setSortBy(key);
      setSortOrder("asc");
    }
  };
  const ActiveSortCol = props.IsSortable && props.IsPaged && TotalPages > 1 ? props.strSortCol : sortBy;
  const ActiveSortOrder = props.IsSortable && props.IsPaged && TotalPages > 1 ? props.strSortOrder : sortOrder;
  const Compare_Values = (a, b, strType) => {
    const t = Type_Of(strType);
    if (t === "decimal") {
      const da = Dec_Parse(a);
      const db = Dec_Parse(b);
      return Dec_Cmp(da === null ? DEC_ZERO : da, db === null ? DEC_ZERO : db);
    }
    if (t === "DateTime") return Date_Value(a) - Date_Value(b);
    return String(a === null || a === undefined ? "" : a).localeCompare(String(b === null || b === undefined ? "" : b), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  };
  const IsServerSort = Boolean(props.IsSortable && props.IsPaged && TotalPages > 1);
  // sorted only when data / sort change, not on every keystroke elsewhere on the screen
  const Sorted_Report = useMemo(() => {
    if (!sortBy || IsServerSort) return ReportData;
    const SortDesign = ReportDesing.find((d) => d.strRptColName === sortBy);
    const strType = SortDesign ? SortDesign.strDatatype : "";
    return ReportData.slice().sort((a, b) => {
      const iRes = Compare_Values(a[sortBy], b[sortBy], strType);
      return sortOrder === "asc" ? iRes : -iRes;
    });
  }, [ReportData, ReportDesing, sortBy, sortOrder, IsServerSort]);
  //#endregion

  //#region Formatting
  const textAlign = ["left", "center", "right"];
  const fontWeight = ["normal", "500", "bold"];
  const converttoColor = (Value, type) => {
    if (Type_Of(type) !== "decimal") return "black";
    return Dec_IsNeg(Dec_Parse(Value)) ? "red" : "black";
  };
  const converttodecimal = (Value, type) => {
    if (Type_Of(type) !== "decimal") return Value;
    if (Value === null || Value === undefined || String(Value).trim() === "") return "";
    const strRounded = Round2(Value);
    return strRounded === null ? Value : strRounded;
  };
  const Sum_Value = (objSum, design) => {
    if (!objSum) return "";
    const Value = objSum[design.strAliasName];
    if (Value === undefined || Value === null || String(Value).trim() === "") return "";
    const strRounded = Round2(Value);
    return strRounded === null ? String(Value) : strRounded;
  };
  const Table_Style = (design, report) => {
    const Style = {
      textAlign: textAlign[Number(design.iFontAlign) - 1] || "left",
      fontWeight: fontWeight[Number(design.iFontStyle) - 1] || "normal",
      whiteSpace: "wrap",
      color: converttoColor(report[design.strRptColName], design.strDatatype),
    };
    const strWidth = String(design.strWidth === undefined || design.strWidth === null ? "auto" : design.strWidth).trim() || "auto";
    if (strWidth.includes("%") || strWidth.includes("auto")) {
      Style.width = strWidth;
      Style.minWidth = "100px";
    } else {
      Style.width = strWidth + "px";
      Style.minWidth = strWidth + "px";
      Style.maxWidth = strWidth + "px";
    }
    return Style;
  };
  //#endregion

  //#region Export (all rows from the server, not just the page on screen)
  const Load_ExportData = async () => {
    if (Number(props.iRptId) === 0) {
      AlertMessage("Please Select Report", "info");
      return null;
    }
    if (IsPartyPending) {
      AlertMessage("Please select a party", "info");
      return null;
    }
    if (IsLedgerPending) {
      AlertMessage("Please select a ledger", "info");
      return null;
    }
    const objData = await props.Download_Report();
    if (!objData) return null;
    const arrDesign = objData.Design && objData.Design.length > 0 ? objData.Design : ReportDesing;
    if (objData.Data.length === 0) {
      AlertMessage("No rows to export", "info");
      return null;
    }
    let objBranch = {};
    const res = await API_FETCH(
      `Reports/View_BranchInfo/${Number(props.iRptId)}/${
        Number(props.iBranchId) || Number(_AuthParem.Branch)
      }/${_AuthParem.DB}`,
      dispatch
    );
    if (Is_ValidResponse(res, dispatch) && Array.isArray(res.objBranch) && res.objBranch.length > 0)
      objBranch = res.objBranch[0];
    const strTitle = String(objBranch.strCompName || objBranch.strBranchName || "").toUpperCase();
    const strBranch =
      objBranch.strBranchName && objBranch.strCompName && objBranch.strBranchName !== objBranch.strCompName
        ? String(objBranch.strBranchName)
        : "";
    const Address1 = [objBranch.strAdd1, objBranch.strAdd2].filter((s) => s && String(s).trim()).join(", ");
    const Address2 = [objBranch.strAdd3, objBranch.strAdd4].filter((s) => s && String(s).trim()).join(", ");
    return { ...objData, Design: arrDesign, strTitle, strBranch, Address1, Address2 };
  };
  const Export_Guard = async (fnExport) => {
    if (IsExporting) return;
    setIsExporting(true);
    try {
      const objExport = await Load_ExportData();
      if (objExport) fnExport(objExport);
    } catch (err) {
      AlertMessage(err.message, "error");
    } finally {
      setIsExporting(false);
    }
  };
  const Save_File = (objBlob, strFileName) => {
    const url = URL.createObjectURL(objBlob);
    const Link = document.createElement("a");
    Link.href = url;
    Link.download = strFileName;
    document.body.appendChild(Link);
    Link.click();
    document.body.removeChild(Link);
    setTimeout(() => URL.revokeObjectURL(url), 1000); // free the blob
  };
  const File_Name = () => String(props.strReportName || "Report").replace(/[\\/:*?"<>|]/g, "_");
  const DownLoad_Excel = () =>
    Export_Guard((objExport) => {
      const { Data, Summary, Design } = objExport;
      const strHead = "font-family:'Times New Roman';color:darkgreen;font-weight:bold;text-align:center;";
      const strCell = "border:1px solid black;font-size:13px;font-family:'Times New Roman';";
      let strHtml = `<html><head><meta charset="utf-8"></head><body>`;
      const Line = (txt) => (txt ? `<p style="${strHead}">${Escape_Html(txt)}</p>` : "");
      strHtml += Line(objExport.strTitle) + Line(objExport.strBranch) + Line(objExport.Address1) + Line(objExport.Address2);
      strHtml += Line(String(props.strReportName || "").toUpperCase());
      strHtml += `<table><thead><tr>`;
      Design.forEach((design) => {
        strHtml += `<th style="${strCell}color:#1b5e20;text-align:center;font-weight:bold;">${Escape_Html(design.strAliasName)}</th>`;
      });
      strHtml += `</tr></thead><tbody>`;
      Data.forEach((report) => {
        strHtml += "<tr>";
        Design.forEach((design) => {
          const IsDecimal = Type_Of(design.strDatatype) === "decimal";
          // text columns stay text in Excel (keeps leading zeros of codes / phone numbers)
          const strFormat = IsDecimal ? "mso-number-format:'0.00';text-align:right;" : "mso-number-format:'\\@';";
          strHtml += `<td style="${strCell}${strFormat}">${Escape_Html(
            converttodecimal(report[design.strRptColName], design.strDatatype)
          )}</td>`;
        });
        strHtml += "</tr>";
      });
      strHtml += "</tbody>";
      if (Summary.length > 0) {
        strHtml += "<tfoot><tr>";
        Design.forEach((design) => {
          strHtml += `<td style="${strCell}color:#1b5e20;font-weight:bold;text-align:right;">${Escape_Html(
            Sum_Value(Summary[0], design)
          )}</td>`;
        });
        strHtml += "</tr></tfoot>";
      }
      strHtml += "</table></body></html>";
      Save_File(new Blob(["﻿" + strHtml], { type: "application/vnd.ms-excel;charset=utf-8" }), File_Name() + ".xls");
    });
  const DownLoad_Pdf = () =>
    Export_Guard((objExport) => {
      const { Data, Summary, Design } = objExport;
      const doc = new jsPDF(Design.length > 8 ? "l" : "p", "pt");
      const iWidth = doc.internal.pageSize.getWidth();
      const iCenter = iWidth / 2;
      const style = { align: "center", baseline: "middle" };
      const RptName = String(props.strReportName || "").toUpperCase();
      const currdate = "Date : " + Today_String().split("-").reverse().join("-");
      const currtime = "Time : " + new Date().toLocaleTimeString();
      let iY = 20;
      doc.setTextColor("black");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      if (objExport.strTitle) {
        doc.text(objExport.strTitle, iCenter, iY, style);
        iY += 18;
      }
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      [objExport.strBranch, objExport.Address1, objExport.Address2].forEach((txt) => {
        if (txt) {
          doc.text(String(txt), iCenter, iY, style);
          iY += 15;
        }
      });
      doc.setTextColor("darkgreen");
      doc.setFont("helvetica", "bold");
      doc.text(RptName, iCenter, iY, style);
      doc.setTextColor("black");
      doc.setFont("helvetica", "normal");
      doc.text(currdate, iWidth - 80, 40, style);
      doc.text(currtime, iWidth - 80, 55, style);
      const arrDecimalCol = {};
      Design.forEach((design, Index) => {
        if (Type_Of(design.strDatatype) === "decimal") arrDecimalCol[Index] = { halign: "right" };
      });
      autoTable(doc, {
        margin: { top: 10, left: 10, right: 10, bottom: 10 },
        startY: iY + 15,
        theme: "grid",
        showFoot: "lastPage",
        showHead: "everyPage",
        rowPageBreak: "auto",
        tableWidth: "auto",
        pageBreak: "auto",
        horizontalPageBreak: true,
        horizontalPageBreakRepeat: 0,
        head: [Design.map((design) => String(design.strAliasName === null || design.strAliasName === undefined ? "" : design.strAliasName))],
        body: Data.map((report) =>
          Design.map((design) => {
            const Value = converttodecimal(report[design.strRptColName], design.strDatatype);
            return Value === null || Value === undefined ? "" : String(Value);
          })
        ),
        foot: Summary.length > 0 ? [Design.map((design) => Sum_Value(Summary[0], design))] : undefined,
        columnStyles: arrDecimalCol,
        headStyles: { textColor: "darkgreen", fontStyle: "bold", halign: "center", fillColor: "white" },
        bodyStyles: { textColor: "black", fontStyle: "normal", overflow: "linebreak" },
        footStyles: { textColor: "black", fontStyle: "bold", halign: "right", fillColor: "white" },
        styles: { lineColor: "black", lineWidth: 0.1, fontSize: 8, fillColor: "white", minCellHeight: 15 },
      });
      // "Page x of y" on every page
      const iPages = doc.internal.getNumberOfPages();
      const iPageHeight = doc.internal.pageSize.getHeight();
      for (let iPg = 1; iPg <= iPages; iPg++) {
        doc.setPage(iPg);
        doc.setFontSize(8);
        doc.text(`Page ${iPg} of ${iPages}`, iWidth - 40, iPageHeight - 8, { align: "right" });
      }
      doc.save(File_Name() + ".pdf");
    });
  const DownLoad_Json = () =>
    Export_Guard((objExport) => {
      const EntData = objExport.Data.map((report) => {
        const ObjRow = {};
        objExport.Design.forEach((design) => {
          const Value = converttodecimal(report[design.strRptColName], design.strDatatype);
          ObjRow[design.strRptColName] =
            Type_Of(design.strDatatype) === "decimal" && Value !== "" && !isNaN(Number(Value)) ? Number(Value) : Value;
        });
        return ObjRow;
      });
      Save_File(new Blob([JSON.stringify(EntData)], { type: "application/json;charset=utf-8" }), File_Name() + ".json");
    });
  //#endregion

  const Hide_Popup = () => {
    setIsColOption(false);
    if (props.IsAccounts) props.setIsLedOption(false);
    else props.Party_OptionShow(false);
    // Branch / Warehouse dropdowns share their open flags with dropdowns inside the filter popup
    dispatch(hide_Options({ isoptionShow: false }));
    dispatch(hide_Options({ isoptionShow1: false }));
  };
  // Page total (rows on screen) next to the grand total (whole filtered result, from the server)
  const ShowPageTotal = props.IsPaged && TotalPages > 1 && EntSum.length > 0;
  const PageTotals = useMemo(() => {
    const objTotals = {};
    if (!ShowPageTotal) return objTotals;
    ReportDesing.forEach((design) => {
      if (Type_Of(design.strDatatype) !== "decimal" || Sum_Value(EntSum[0], design) === "") return;
      let decSum = DEC_ZERO;
      ReportData.forEach((row) => {
        const dec = Dec_Parse(row[design.strRptColName]);
        if (dec !== null) decSum = Dec_Add(decSum, dec);
      });
      objTotals[design.strRptColName] = Dec_Format2(decSum);
    });
    return objTotals;
  }, [ShowPageTotal, ReportData, ReportDesing, EntSum]);
  //#region Filters (panel, badge, empty state)
  const iActiveFilters = Number(props.iActiveFilters) || 0;
  const Filter_Applied = () => {
    if (typeof props.Filter_Applied === "function") props.Filter_Applied();
    else props.Reload_Report();
  };
  const Close_Filter = () => {
    setIsFilter(false);
    // give the focus back to the filter icon (keyboard users)
    setTimeout(() => {
      if (FilterBtnRef.current) FilterBtnRef.current.focus();
    }, 0);
  };
  const Clear_AllFilters = async () => {
    if (FilterBusy.current || !Number(props.iRptId)) return;
    FilterBusy.current = true;
    setIsClearingFilters(true);
    try {
      const res = await API_POST(
        `Reports/Delete_ReportFilter/${Number(props.iRptId)}/0/${Number(props.iBranchId) || Number(_AuthParem.Branch) || 0}/${_AuthParem.DB}${UserQs}`,
        {},
        dispatch
      );
      if (Is_ValidResponse(res, dispatch)) {
        AlertMessage(res.strMessage || "All filters cleared", "success");
        Filter_Applied();
      }
    } catch (e) {
      AlertMessage(e.message, "error");
    } finally {
      FilterBusy.current = false;
      setIsClearingFilters(false);
    }
  };
  // chip "x": removes every saved filter of that column, then page 1 with the new totals
  const Remove_Filter = async (objFilter) => {
    if (FilterBusy.current || !Number(props.iRptId) || !objFilter) return;
    FilterBusy.current = true;
    setstrRemovingCol(String(objFilter.iRptColId));
    try {
      const res = await API_POST(
        `Reports/Reset_ReportFilter/${Number(props.iRptId)}/${Number(objFilter.iRptColId)}/${_AuthParem.DB}${UserQs}`,
        {},
        dispatch
      );
      if (Is_ValidResponse(res, dispatch)) {
        AlertMessage(res.strMessage || "Filter removed", "success");
        Filter_Applied();
      }
    } catch (e) {
      AlertMessage(e.message, "error");
    } finally {
      FilterBusy.current = false;
      setstrRemovingCol("");
    }
  };
  const Clear_TableSearch = () => {
    clearTimeout(TableSearchTimer.current);
    setstrtableSearch("");
    props.Search_Report("", props.FilterColName);
  };
  // Message row inside the existing grid (no new styles: existing table cell + btn-fabgreen buttons)
  const Empty_State = () => {
    // pinned to the visible left edge: on wide reports the cell spans every column and scrolls sideways
    const objStyle = { position: "sticky", left: 0, maxWidth: 640, textAlign: "left", padding: "24px 12px", whiteSpace: "normal" };
    const objBtn = { width: "auto", display: "inline-block", marginRight: 8, marginTop: 4 };
    if (props.IsLoading) return <p style={objStyle}>Loading report...</p>;
    if (IsPartyPending) return <p style={objStyle}>Select a party to view this report.</p>;
    if (IsLedgerPending) return <p style={objStyle}>Select a ledger to view the General Ledger.</p>;
    const IsSearching = String(strtableSearch || "").trim().length > 0;
    // exactly what the server applied (saved filters + report setting condition), from the app's own database
    const arrApplied = Array.isArray(props.EntAppliedFilters) ? props.EntAppliedFilters : [];
    const Applied_List =
      arrApplied.length > 0 ? (
        <ul style={{ margin: "4px 0 8px", paddingLeft: 18 }}>
          {arrApplied.map((strText, i) => (
            <li key={i}>{strText}</li>
          ))}
        </ul>
      ) : null;
    if (iActiveFilters > 0 || IsSearching)
      return (
        <div style={objStyle} role="status">
          <p>
            <strong>No data matches your filter criteria</strong>
          </p>
          <p>
            {iActiveFilters > 0 ? `${iActiveFilters} filter${iActiveFilters === 1 ? " is" : "s are"} applied` : ""}
            {iActiveFilters > 0 && IsSearching ? " and " : ""}
            {IsSearching ? "a search is active" : ""}
            {props.IsDateFilter ? ". Try a wider date range, " : ". Try "}removing a filter or clearing the search.
          </p>
          {Applied_List}
          <div>
            {iActiveFilters > 0 && !props.IsFilterHidden && (
              <button className="btn-fabgreen" style={objBtn} disabled={IsClearingFilters} onClick={Clear_AllFilters}>
                {IsClearingFilters ? "Clearing..." : "Clear All Filters"}
              </button>
            )}
            {iActiveFilters > 0 && !props.IsFilterHidden && (
              <button className="btn-fabgreen" style={objBtn} onClick={() => setIsFilter(true)}>
                Edit Filters
              </button>
            )}
            {IsSearching && (
              <button className="btn-fabgreen" style={objBtn} onClick={Clear_TableSearch}>
                Clear Search
              </button>
            )}
          </div>
        </div>
      );
    return (
      <div style={objStyle} role="status">
        <p>{props.IsDateFilter ? "No records found for the selected period." : "No records found."}</p>
        {Applied_List}
      </div>
    );
  };
  //#endregion
  const Footer_Label = (Index, strLabel, fnHasValue) =>
    Index === 0 && !fnHasValue(ReportDesing[0]) ? strLabel : null;

  // removable filter chips (reports where filtering is allowed)
  const EntActiveFilters = Array.isArray(props.EntActiveFilters) ? props.EntActiveFilters : [];
  const ShowChips = !props.IsFilterHidden && Number(props.iRptId) > 0 && EntActiveFilters.length > 0;
  const Chip_Style = (IsApplied) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 4px 2px 10px",
    border: "1px solid " + (IsApplied ? "#0d6efd" : "#adb5bd"),
    borderRadius: 14,
    fontSize: 12,
    lineHeight: "20px",
    color: IsApplied ? "#0b3d91" : "#6c757d",
    background: IsApplied ? "#e7f1ff" : "#f8f9fa",
  });

  const RowFrom = TotalRows === 0 ? 0 : props.IsPaged ? (CurrPage - 1) * RowPerPage + 1 : 1;
  const RowTo = props.IsPaged ? Math.min(CurrPage * RowPerPage, TotalRows) : TotalRows;
  const EntReports = Array.isArray(props.EntReports) ? props.EntReports : [];
  const EntParty = Array.isArray(props.EntParty) ? props.EntParty : [];
  const EntLedgerOption = Array.isArray(props.EntLedgerOption) ? props.EntLedgerOption : [];

  return (
    <div className="Master-container">
      <div className="Master-card">
        <div className="m-header ">
          <div className="m-hsearch">
            <input
              type="search"
              placeholder="Search Reports"
              value={strRptSearch}
              onChange={(e) => Search_ReportList(e.target.value)}
              autoFocus
            />
            <i
              className="fas fa-search"
              onClick={(e) => {
                clearTimeout(RptSearchTimer.current);
                props.Load_Reports(strRptSearch);
              }}
            ></i>
          </div>
        </div>
        <div className="data-list rpt">
          {EntReports.map((report, Index) => (
            <button
              key={report.iId !== undefined ? report.iId : Index}
              tabIndex={Index}
              className={Number(report.iId) === Number(props.iRptId) ? "active" : ""}
              onKeyDown={(e) => List_KeyDown(e, Index, EntReports.length, () => Open_Report(report))}
              onClick={(e) => Open_Report(report)}
            >
              <label>{report.strName}</label>
            </button>
          ))}
        </div>
      </div>
      <div className="Master-card rpt-print-area">
        <style>{Print_Css}</style>
        <div className="rpt-print-only">
          <h3>{String(props.strReportName || "").toUpperCase()}</h3>
          <p>
            {props.IsDateFilter && props.strFromDate && props.strToDate
              ? `From ${props.strFromDate.split("-").reverse().join("-")} To ${props.strToDate.split("-").reverse().join("-")}  |  `
              : ""}
            Printed {Today_String().split("-").reverse().join("-")} {new Date().toLocaleTimeString()}
            {props.IsPaged && TotalPages > 1 ? `  |  Page ${CurrPage} of ${TotalPages} (use PDF for all rows)` : ""}
          </p>
        </div>
        <div className="Master-wrapper report-wrapper">
          <div className="row report-header">
            <div className="rport-title mt-1">
              <strong>{String(props.strReportName || "").toLocaleUpperCase()}</strong>
              {Number(props.iRptId) > 0 && (
                <div className="icon-btn-grp">
                  <div className="lbltooltip" onClick={(e) => { e.preventDefault(); DownLoad_Excel(); }}>
                    <a className="icon-btn " href="##" onClick={(e) => e.preventDefault()}>
                      <img src={Excel} alt="Excel Icon" />
                    </a>
                    <span className="tooltiptext">{IsExporting ? "Please wait..." : "Excel"}</span>
                  </div>
                  <div className="lbltooltip" onClick={(e) => { e.preventDefault(); DownLoad_Pdf(); }}>
                    <a className="icon-btn " href="##" onClick={(e) => e.preventDefault()}>
                      <i className="far fa-file-pdf"></i>
                    </a>
                    <span className="tooltiptext">{IsExporting ? "Please wait..." : "Pdf"}</span>
                  </div>
                  <div className="lbltooltip" onClick={(e) => { e.preventDefault(); DownLoad_Json(); }}>
                    <a className="icon-btn" href="##" onClick={(e) => e.preventDefault()}>
                      <i className="bx bxs-file-json"></i>
                    </a>
                    <span className="tooltiptext">{IsExporting ? "Please wait..." : "Json Format"}</span>
                  </div>
                  {/* accounting statements (Ledger, Trading, P&L, Balance Sheet, Trial Balance) are never column-filtered */}
                  {!props.IsFilterHidden && (
                  <div
                    className="lbltooltip"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsFilter(!IsFilter);
                      Hide_Popup();
                    }}
                  >
                    <a
                      ref={FilterBtnRef}
                      className={"icon-btn" + (iActiveFilters > 0 ? " active" : "")}
                      href="##"
                      aria-haspopup="dialog"
                      aria-expanded={IsFilter}
                      aria-label={iActiveFilters > 0 ? "Filter, " + iActiveFilters + " active" : "Filter"}
                      onClick={(e) => e.preventDefault()}
                    >
                      <i
                        className="fa-solid fa-arrow-down-short-wide"
                        style={iActiveFilters > 0 ? { color: "#0d6efd" } : undefined}
                      ></i>
                    </a>
                    <span className="tooltiptext">{iActiveFilters > 0 ? `Filter (${iActiveFilters} active)` : "Filter"}</span>
                  </div>
                  )}
                  <div
                    className="lbltooltip"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsSetting(!IsSetting);
                      Hide_Popup();
                    }}
                  >
                    <a className="icon-btn" href="##" onClick={(e) => e.preventDefault()}>
                      <i className="fa-solid fa-sliders"></i>
                    </a>
                    <span className="tooltiptext">Setting</span>
                  </div>
                  <div className="lbltooltip">
                    <a className="icon-btn " href="##" onClick={(e) => e.preventDefault()}>
                      <i
                        className="fa-regular fa-rectangle-xmark"
                        onClick={(e) => (window.location.href = "/JustBill/Dashboard")}
                      ></i>
                    </a>
                    <span className="tooltiptext">Close</span>
                  </div>
                </div>
              )}
            </div>
            <div className="col-sm-6 col-md-4 mt-2 pl-0">
              {/* consolidated books: statements cover every branch, so the box is locked */}
              <CustomizedSelectoption
                optionparem="isoptionShow"
                showoption={props.IsBranchLocked ? false : _select.isoptionShow}
                placeholder={props.IsBranchLocked ? props.strBranchLockText || "All Branches" : "Select Branch"}
                select_value={(val) => props.Branch_Onchange(val)}
                btnname="Branch"
                btnshow={false}
                show_popup=""
                valueonly={false}
                defaultval={props.IsBranchLocked ? "" : props.iBranchId}
                displayName="strBranchName"
                disvalue="iBranchId"
                arrydata={props.arrBranch}
                disabled={Boolean(props.IsBranchLocked)}
                EmptVal="Branch"
              />
            </div>
            <div className="col-sm-6 col-md-4 mt-2 pl-0">
              <CustomizedSelectoption
                optionparem="isoptionShow1"
                showoption={_select.isoptionShow1}
                placeholder="Select Warehouse"
                select_value={(val) => {
                  props.setiWHId(val);
                }}
                btnname="Warehouse"
                btnshow={false}
                show_popup=""
                valueonly={true}
                defaultval={props.iWHId}
                displayName="strWHName"
                disvalue="iWHId"
                arrydata={props.arrWH}
                disabled={props.IsWHdisable}
                EmptVal="Warehouse"
              />
            </div>
            {!props.IsAccounts && (
              <div className="col-sm-6 col-md-4 mt-2 pl-0">
                <div
                  className="custom-select"
                  onMouseLeave={(e) => props.Party_OptionShow(false)}
                >
                  <input
                    className="form-control"
                    disabled={!props.IsParty}
                    type="text"
                    placeholder="Select  Party Name"
                    value={props.PartyName || ""}
                    readOnly
                    onClick={(e) => {
                      props.Party_OptionShow(!props.IsPartyOption);
                      setstrCustSearch("");
                      props.Search_Party("");
                    }}
                  />
                  <i className="fa-solid fa-caret-down"></i>
                  {props.IsPartyOption && (
                    <div className="custom-option">
                      <div className="search-option">
                        <input
                          className="form-control"
                          type="text"
                          autoFocus
                          placeholder="Search Party"
                          value={strCustSearch}
                          onChange={(e) => Search_PartyText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "ArrowDown") {
                              e.preventDefault();
                              const objList = e.currentTarget.closest(".custom-option");
                              const objFirst = objList && objList.querySelector('.option-list [tabindex="0"]');
                              if (objFirst) objFirst.focus();
                            } else if (e.key === "Enter") {
                              e.preventDefault();
                              Select_Party(EntParty[0]);
                            }
                          }}
                        />
                        {EntParty.length === 0 && <span> Party Not Found</span>}
                      </div>
                      <div className="option-list">
                        {EntParty.map((Party, Index) => (
                          <button
                            key={Index}
                            tabIndex={Index}
                            onClick={(e) => Select_Party(Party)}
                            onKeyDown={(e) => List_KeyDown(e, Index, EntParty.length, () => Select_Party(Party))}
                            className="con-btn"
                          >
                            {Party.partyName}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {props.IsAccounts && (
              <div className="col-sm-6 col-md-4 mt-2 pl-0">
                <div
                  className="custom-select"
                  onMouseLeave={(e) => props.setIsLedOption(false)}
                >
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Select Ledger"
                    value={props.strLedger || ""}
                    readOnly
                    onClick={(e) => {
                      props.setIsLedOption(true);
                      Search_Ledger("");
                    }}
                    disabled={Number(props.iRptId) !== 1015}
                  />
                  <i className="fa-solid fa-caret-down"></i>
                  {props.IsLedOption && (
                    <div className="custom-option">
                      <div className="search-option">
                        <input
                          className="form-control"
                          type="text"
                          autoFocus
                          placeholder="Search Ledger"
                          value={strLedSearch}
                          onChange={(e) => Search_Ledger(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "ArrowDown") {
                              e.preventDefault();
                              const objList = e.currentTarget.closest(".custom-option");
                              const objFirst = objList && objList.querySelector('.option-list [tabindex="0"]');
                              if (objFirst) objFirst.focus();
                            } else if (e.key === "Enter" && EntLedgerOption.length > 0) {
                              e.preventDefault();
                              View_Ledger(EntLedgerOption[0].strName);
                            }
                          }}
                        />
                        {EntLedgerOption.length === 0 && <span>Ledger Not Found</span>}
                      </div>
                      <div className="option-list">
                        {EntLedgerOption.map((ledger, Index) => (
                          <button
                            key={Index}
                            tabIndex={Index}
                            onClick={(e) => View_Ledger(ledger.strName)}
                            onKeyDown={(e) =>
                              List_KeyDown(e, Index, EntLedgerOption.length, () => View_Ledger(ledger.strName))
                            }
                            className="con-btn"
                          >
                            {ledger.strName}
                            {(ledger.strCode || ledger.strAlias) && (
                              <small style={{ display: "block", opacity: 0.7 }}>
                                {[ledger.strCode, ledger.strAlias].filter((v) => v && String(v).trim()).join("  ·  ")}
                              </small>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="col-sm-6 col-md-4 mt-2 pl-0">
              <input
                className="form-control rounded"
                type="date"
                title="From Date"
                value={props.strFromDate || ""}
                min={props.strMinDate || undefined}
                max={props.strToDate || undefined}
                onChange={(e) => Date_Onchange(e.target.value, "FromDate")}
                disabled={!props.IsDateFilter}
              />
            </div>
            <div className="col-sm-6 col-md-4 mt-2 pl-0">
              <input
                className="form-control rounded"
                type="date"
                title="To Date"
                value={props.strToDate || ""}
                min={props.strFromDate || undefined}
                onChange={(e) => Date_Onchange(e.target.value, "ToDate")}
                disabled={!props.IsDateFilter}
              />
            </div>
            <div className="col-sm-6 col-md-4 mt-2 pl-0">
              <div
                className="custom-select"
                onMouseLeave={(e) => setIsColOption(false)}
              >
                <input
                  className="form-control"
                  type="text"
                  placeholder="Search Column"
                  value={FilterColAlias()}
                  readOnly
                  onClick={(e) => {
                    setIsColOption(!IsColOption);
                    Search_FilterCol("");
                  }}
                  disabled={Number(props.iRptId) === 0}
                />
                <i
                  className="fa-solid fa-caret-down"
                  style={{
                    position: "absolute",
                    right: "10px",
                    color: "darkgreen",
                    transform: IsColOption ? "rotate(-180deg)" : "rotate(0deg)",
                  }}
                ></i>
                {IsColOption && (
                  <div className="custom-option">
                    <div className="search-option">
                      <input
                        className="form-control"
                        type="text"
                        autoFocus
                        placeholder="Search Filter Column"
                        value={strColSearch}
                        onChange={(e) => Search_FilterCol(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "ArrowDown") {
                            e.preventDefault();
                            const objList = e.currentTarget.closest(".custom-option");
                            const objFirst = objList && objList.querySelector('.option-list [tabindex="0"]');
                            if (objFirst) objFirst.focus();
                          } else if (e.key === "Enter" && RptColumn.length > 0) {
                            e.preventDefault();
                            Save_SearchColumn(RptColumn[0]);
                          }
                        }}
                      />
                      {RptColumn.length === 0 && <span> Column Not Found</span>}
                    </div>
                    <div className="option-list">
                      {RptColumn.map((col, Index) => (
                        <button
                          key={Index}
                          tabIndex={Index}
                          onClick={(e) => Save_SearchColumn(col)}
                          onKeyDown={(e) => List_KeyDown(e, Index, RptColumn.length, () => Save_SearchColumn(col))}
                          className="con-btn"
                        >
                          {col.strAliasName}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="col-sm-6 col-md-6 mt-2 pl-0">
              <div className="m-hsearch">
                <input
                  type="search"
                  placeholder="Search in Report"
                  value={strtableSearch}
                  disabled={Number(props.iRptId) === 0 || IsPartyPending || IsLedgerPending}
                  onChange={(e) => Search_Table(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") Run_TableSearch(strtableSearch);
                  }}
                />
                <i
                  className="fas fa-search"
                  onClick={(e) => Run_TableSearch(strtableSearch)}
                ></i>
              </div>
            </div>
          </div>
          {ShowChips && (
            <div
              className="rpt-filter-chips"
              role="list"
              aria-label="Active filters"
              style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, margin: "6px 0" }}
            >
              {EntActiveFilters.map((objF, iIndex) => {
                const IsApplied = objF.IsApplied !== false;
                const strAlias = String(objF.strAliasName || objF.strColName || "");
                return (
                  <span
                    key={`${objF.iRptColId}-${objF.strKind}-${iIndex}`}
                    role="listitem"
                    style={Chip_Style(IsApplied)}
                    title={IsApplied ? strAlias + ": " + objF.strSummary : "Saved but not applied to this report"}
                  >
                    <strong>{strAlias}</strong>&nbsp;{objF.strSummary}
                    <button
                      type="button"
                      aria-label={"Remove filter on " + strAlias}
                      disabled={strRemovingCol !== "" || IsClearingFilters}
                      onClick={() => Remove_Filter(objF)}
                      style={{ border: 0, background: "transparent", cursor: "pointer", padding: "0 6px", fontSize: 14, lineHeight: "18px" }}
                    >
                      {strRemovingCol === String(objF.iRptColId) ? "…" : "×"}
                    </button>
                  </span>
                );
              })}
              {EntActiveFilters.length > 1 && (
                <button
                  type="button"
                  className="btn-fabgreen"
                  style={{ width: "auto", padding: "2px 10px" }}
                  disabled={IsClearingFilters || strRemovingCol !== ""}
                  onClick={Clear_AllFilters}
                >
                  {IsClearingFilters ? "Clearing..." : "Clear all"}
                </button>
              )}
            </div>
          )}
          <div className="table-card" style={{ overflowX: "auto" }}>
            <table className="option-list" id="Reporttable">
              <thead>
                <tr>
                  {ReportDesing.map((design, Index) => (
                    <th
                      key={Index}
                      className={ActiveSortCol === design.strRptColName ? "active" : ""}
                      style={{ cursor: props.IsSortLocked ? "default" : "pointer" }}
                      title={props.IsSortLocked ? undefined : "Click to sort"}
                      onClick={(e) => Sort_Report(design)}
                    >
                      {design.strAliasName}
                      {ActiveSortCol === design.strRptColName && (
                        <i
                          className={ActiveSortOrder === "desc" ? "fa-solid fa-sort-down" : "fa-solid fa-sort-up"}
                          style={{ marginLeft: "4px" }}
                        ></i>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Sorted_Report.length === 0 && (ReportDesing.length > 0 || IsLedgerPending) && (
                  <tr>
                    <td colSpan={Math.max(1, ReportDesing.length)}>
                      {Empty_State()}
                    </td>
                  </tr>
                )}
                {Sorted_Report.map((report, trIndex) => (
                  <tr key={trIndex}>
                    {ReportDesing.map((design, tdindex) => (
                      <td key={tdindex} style={Table_Style(design, report)}>
                        {converttodecimal(report[design.strRptColName], design.strDatatype)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {ShowPageTotal && (
                  <tr className="rpt-page-total">
                    {ReportDesing.map((design, Index) => (
                      <td key={Index} style={{ textAlign: "right" }}>
                        {Footer_Label(Index, "Page Total", (d) => PageTotals[d.strRptColName] !== undefined) ||
                          (PageTotals[design.strRptColName] !== undefined ? PageTotals[design.strRptColName] : "")}
                      </td>
                    ))}
                  </tr>
                )}
                <tr>
                  {ReportDesing.map((design, Index) => (
                    <td key={Index}>
                      {(ShowPageTotal && Footer_Label(Index, "Grand Total", (d) => Sum_Value(EntSum[0], d) !== "")) ||
                        (EntSum.length > 0 ? Sum_Value(EntSum[0], design) : "")}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
        <div className="rpt-section">
          <p>
            Total Records : {TotalRows}
            {props.IsPaged && TotalPages > 1 && ` (showing ${RowFrom} - ${RowTo})`}
          </p>
          {EntSum.length > 0 &&
            ReportDesing.map(
              (design, Index) =>
                Sum_Value(EntSum[0], design) !== "" && (
                  <p key={Index}>
                    <span>
                      {design.strAliasName} : &nbsp;
                      {Sum_Value(EntSum[0], design)}
                    </span>
                  </p>
                )
            )}
          {props.IsPaged && TotalPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
              <button className="btn-fabgreen" disabled={CurrPage <= 1} onClick={() => Go_Page(1)} title="First page">
                <i className="fa-solid fa-angles-left"></i>
              </button>
              <button className="btn-fabgreen" disabled={CurrPage <= 1} onClick={() => Go_Page(CurrPage - 1)} title="Previous page">
                <i className="fa-solid fa-angle-left"></i>
              </button>
              <span>Page</span>
              <input
                type="number"
                className="form-control"
                style={{ width: "75px", display: "inline-block" }}
                min={1}
                max={TotalPages}
                value={strPageInput}
                onClick={(e) => e.target.select()}
                onChange={(e) => setstrPageInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") Go_Page(strPageInput);
                }}
                onBlur={(e) => Go_Page(strPageInput)}
              />
              <span>of {TotalPages}</span>
              <button className="btn-fabgreen" disabled={CurrPage >= TotalPages} onClick={() => Go_Page(CurrPage + 1)} title="Next page">
                <i className="fa-solid fa-angle-right"></i>
              </button>
              <button className="btn-fabgreen" disabled={CurrPage >= TotalPages} onClick={() => Go_Page(TotalPages)} title="Last page">
                <i className="fa-solid fa-angles-right"></i>
              </button>
            </div>
          )}
        </div>
        {IsSetting && (
          <ReportSetting
            Close={(IsUpdated) => {
              setIsSetting(false);
              if (IsUpdated) props.Reload_Report();
            }}
            iRptId={props.iRptId}
          />
        )}
        {IsFilter && !props.IsFilterHidden && Number(props.iRptId) > 0 && (
          <ReportFilter
            key={props.iRptId}
            Close={Close_Filter}
            Applied={Filter_Applied}
            iRptId={props.iRptId}
            iBranchId={props.iBranchId}
            iWHId={props.iWHId}
            strFinFromDate={props.strFinFromDate}
            strReportName={props.strReportName}
          />
        )}
        <Messagedialogbox />
      </div>
    </div>
  );
}

export default ReportTable;